/**
 * Shared player strings surfaced by `@workspace/player-core` (consumed by web,
 * mobile, tv, and the server) — kept tiny and framework-agnostic. Exposed via
 * the `/common` surface so the pure-logic player lib can resolve them.
 *
 * These are the now-playing "reason" labels shown on the player surface.
 */
export const player = {
  idle: "Idle",
  music: "Music",
  musicLabeled: "Music · {name}",
  announcement: "Announcement",
  announcementLabeled: "Announcement · {label}",
  adBreak: "Ad break",
  adBreakLabeled: "Ad break · {label}",

  // Source label for an item started manually (not by the schedule).
  manualLabel: "Manual",
  // Pseudo-playlist name shown for one-off library takeovers.
  libraryLabel: "Library",

  // Title/label of the pre-announcement attention tone queue item
  // (player-core announcement-tone.ts — shared by web + native shells).
  attentionTone: "Attention tone",

  // ── offline cache status badge ──
  // The venue-facing answer to "why is this screen still playing?". Shown by
  // the tv kiosk (artifacts/tv/app/player.tsx) and re-exported to the web
  // player through `playerUi.status.*`, so the two surfaces resolve the SAME
  // literal and cannot drift into differently-worded offline copy.
  cacheStatus: {
    /** Stale schedule, device still reachable. */
    playingFromCache: "Playing from cache",
    /** Device cut off from the server; playback continues from disk. */
    offlinePlayingFromCache: "Offline · playing from cache",
  },

  // ── screen pairing short code (CB-060) ──
  // Shown on every signed-out player surface (web login, tv activate, mobile
  // pair) next to / instead of the QR, so an operator across the room can
  // type it on the dashboard: Sites (sidebar) → "Enter code" (page header
  // button, artifacts/web/src/pages/sites.tsx). There is no Fleet page any more.
  pairing: {
    orEnterCode: "or enter this code",
    codeHint: "In OmniMedia, go to Sites, then Enter code.",
  },

  // ── kiosk exit-PIN flow ──
  // Shared by every kiosk surface: the web dialog
  // (hooks/kiosk-exit-context.tsx) and the native PIN pad
  // (@workspace/player-shell signage-native).
  exit: {
    dialogTitle: "Exit player mode",
    dialogDescription: "Enter the kiosk exit PIN to leave this screen.",
    incorrectPin: "Incorrect PIN. Try again.",
    verifyFailed: "Could not verify PIN. Try again.",
    submit: "Unlock",
    cancel: "Cancel",
    deleteDigit: "Delete",
  },

  // ── built-in signage widgets ──
  // Rendered by every player surface: web (player/components/widgets/*),
  // mobile and tv (@workspace/player-shell signage views). The pure config
  // readers live in @workspace/player-shell/signage.
  widgets: {
    unknown: "Unknown widget.",
    countdown: {
      needsTarget: "Countdown widget needs a target date.",
      // Default shown when the countdown target has passed.
      done: "It's time.",
      unit: {
        days: "days",
        hours: "hours",
        minutes: "minutes",
        seconds: "seconds",
      },
    },
    qr: {
      needsUrl: "QR widget needs a URL.",
      // Caption under the rendered code on the native shells (mobile, tv).
      scanHint: "Scan to open",
    },
    ticker: {
      empty: "No ticker items configured.",
    },
    nowPlaying: {
      label: "Now playing",
      idle: "Nothing playing right now",
      upNext: "Up next",
    },
    news: {
      empty: "No headlines right now.",
      loading: "Loading headlines…",
    },
    menu: {
      empty: "Menu not configured.",
    },
    weather: {
      needsLocation: "Weather widget needs a location (lat/lon).",
      loading: "Loading weather…",
      // WMO weather-code short labels.
      condition: {
        clear: "Clear",
        partlyCloudy: "Partly cloudy",
        overcast: "Overcast",
        fog: "Fog",
        drizzle: "Drizzle",
        rain: "Rain",
        snow: "Snow",
        showers: "Showers",
        snowShowers: "Snow showers",
        thunderstorm: "Thunderstorm",
        unknown: "—",
      },
    },
  },
} as const;
